import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import '@dotlottie/player-component'
import tadaSound from '../../../../assets/tada.mp3'
import countdownNumberSound from '../../../../assets/countdown_number.mp3'
import countdownGoSound from '../../../../assets/countdown_go.mp3'

// ---------------------------------------------------------------------
// Countdown Component
// Displays a countdown (3, 2, 1, then GO!) in a large animated font.
// ---------------------------------------------------------------------
interface CountdownProps {
  onComplete: () => void
}

export const Countdown: React.FC<CountdownProps> = ({ onComplete }) => {
  const [count, setCount] = useState<number>(3)
  const [isComplete, setIsComplete] = useState<boolean>(false)
  const [isExiting, setIsExiting] = useState<boolean>(false)
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [showNumber, setShowNumber] = useState<boolean>(false) // New state to control number visibility
  const numberSoundRef = useRef<HTMLAudioElement | null>(null)
  const goSoundRef = useRef<HTMLAudioElement | null>(null)

  // Initialize audio references
  useEffect(() => {
    numberSoundRef.current = new Audio(countdownNumberSound)
    goSoundRef.current = new Audio(countdownGoSound)

    return () => {
      if (numberSoundRef.current) {
        numberSoundRef.current.pause()
        numberSoundRef.current.currentTime = 0
      }
      if (goSoundRef.current) {
        goSoundRef.current.pause()
        goSoundRef.current.currentTime = 0
      }
    }
  }, [])

  // Handle fade in of container
  useEffect(() => {
    const fadeTimer: NodeJS.Timeout = setTimeout(() => {
      setIsVisible(true)

      // Add delay before showing the first number
      const numberTimer: NodeJS.Timeout = setTimeout(() => {
        setShowNumber(true)
        // Play first number sound
        if (numberSoundRef.current) {
          numberSoundRef.current
            .play()
            .catch(err => console.error('Audio play failed:', err))
        }
      }, 400) // Slight delay after container appears

      return () => clearTimeout(numberTimer)
    }, 300)

    return () => clearTimeout(fadeTimer)
  }, [])

  // Handle countdown logic
  useEffect(() => {
    let timer: NodeJS.Timeout

    if (isVisible && showNumber) { // Only start countdown when number is showing
      if (count > 0) {
        timer = setTimeout(() => {
          // Play appropriate sound for next number
          if (count === 1) {
            // Next will be "GO!" so play GO sound
            if (goSoundRef.current) {
              goSoundRef.current
                .play()
                .catch(err => console.error('Audio play failed:', err))
            }
          } else {
            // Play number sound
            if (numberSoundRef.current) {
              numberSoundRef.current.currentTime = 0
              numberSoundRef.current
                .play()
                .catch(err => console.error('Audio play failed:', err))
            }
          }
          setCount(count - 1)
        }, 1000)
      }
      else if (count === 0 && !isComplete) {
        // Call onComplete after GO! is shown
        if (onComplete) onComplete()

        timer = setTimeout(() => {
          setIsExiting(true)
          setTimeout(() => {
            setIsComplete(true)
          }, 800)
        }, 800)
      }
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [count, isComplete, isVisible, isExiting, onComplete, showNumber])

  // Don't render anything if complete
  if (isComplete) return null

  // Get the racing title color for the background (dark maroon)
  const raceTitleColor = "rgb(139, 0, 0)"

  return (
    <div className="flex items-center justify-center">
      <motion.div
        className="flex items-center justify-center w-[260px] h-[260px] rounded-xl"
        style={{
          backgroundColor: raceTitleColor,
          boxShadow: '3px 3px 2px rgba(0, 0, 0, 0.4)'
        }}
        initial={{ opacity: 0 }}
        animate={{
          opacity: isExiting ? 0 : (isVisible ? 1 : 0)
        }}
        transition={{
          duration: 0.4,
          ease: "easeInOut"
        }}
      >
        {/* Container to create masked area with fixed height and hidden overflow */}
        <div
          className="relative h-[110px] w-full flex items-center justify-center overflow-hidden"
        >
          {/* Container for the sliding numbers */}
          <AnimatePresence initial={false} mode="popLayout">
            {showNumber && (
              <motion.div
                key={`count-${count}`}
                className="absolute text-8xl font-bold text-white caprasimo-regular"
                initial={{ y: 100 }}
                animate={{ y: 0 }}
                exit={{ y: -100 }}
                transition={{
                  type: "tween",
                  duration: 0.4,
                  ease: "easeInOut"
                }}
              >
                {count === 0 ? "GO!" : count}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  )
}

// ---------------------------------------------------------------------
// SprintTitle Component
// Displays the sprint title with a fade/scale animation.
// ---------------------------------------------------------------------
interface SprintTitleProps {
  title: string
  onComplete: () => void
}

export const SprintTitle: React.FC<SprintTitleProps> = ({ title, onComplete }) => {
  const [showSubtitle, setShowSubtitle] = useState<boolean>(false)
  const [showTitle, setShowTitle] = useState<boolean>(false)
  const [showReadyText, setShowReadyText] = useState<boolean>(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    // Create audio for tada sound
    audioRef.current = new Audio(tadaSound)

    // Show subtitle first
    setShowSubtitle(true)

    // Play tada sound when subtitle appears
    audioRef.current
      .play()
      .catch(err => console.error('Audio play failed:', err))

    // Show main title after a delay
    const titleTimer: NodeJS.Timeout = setTimeout(() => {
      setShowTitle(true)
    }, 800) // Faster: reduced from 1000ms to 800ms

    // Hide title and subtitle, show ready text
    const readyTextTimer: NodeJS.Timeout = setTimeout(() => {
      setShowSubtitle(false)
      setShowTitle(false)
      setShowReadyText(true)
    }, 2500)  // Faster: reduced from 3000ms to 2500ms

    // Hide ready text and notify parent component
    const hideReadyTimer: NodeJS.Timeout = setTimeout(() => {
      setShowReadyText(false)
      // Notify parent component that we're done
      if (onComplete) onComplete()
    }, 4000)  // Faster: reduced from 5000ms to 4000ms

    return () => {
      clearTimeout(titleTimer)
      clearTimeout(readyTextTimer)
      clearTimeout(hideReadyTimer)

      // Stop and clean up audio
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
    }
  }, [onComplete]) // Add onComplete to dependency array

  return (
    <div className="relative flex items-center justify-center h-full w-full" style={{ zIndex: 20 }}>
      <AnimatePresence>
        {showSubtitle && (
          <motion.h2
            key="subtitle"
            className="absolute text-3xl mb-4 text-center bg-opacity-80 p-2 rounded-md caprasimo-regular race-title-color"
            style={{
              width: '80%',
              maxWidth: '1000px',
              whiteSpace: 'nowrap',
              zIndex: 30,
              textShadow: '2px 2px 1px rgba(0, 0, 0, 0.2)'
            }}
            initial={{ opacity: 0, y: -120 }}
            animate={{ opacity: 1, y: -160 }}
            exit={{ opacity: 0, y: -200 }}
            transition={{ duration: 0.5 }}
          >
            Race To The Finish
          </motion.h2>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showTitle && (
          <motion.h1
            key="title"
            className="absolute text-6xl font-bold mb-4 text-center bg-opacity-80 p-3 rounded-md caprasimo-regular race-title-color"
            style={{
              width: '80%',
              maxWidth: '1000px',
              whiteSpace: 'nowrap',
              zIndex: 30,
              textShadow: '2px 2px 1px rgba(0, 0, 0, 0.2)'
            }}
            initial={{ opacity: 0, y: -80 }}
            animate={{ opacity: 1, y: -100, scale: 1.2 }}
            exit={{ opacity: 0, y: -160, scale: 1.8 }}
            transition={{ duration: 0.6 }}
          >
            {title}
          </motion.h1>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showReadyText && (
          <motion.h1
            key="readyText"
            className="absolute text-3xl font-bold mb-4 text-center bg-opacity-80 p-2 rounded-md caprasimo-regular race-title-color"
            style={{
              width: '80%',
              maxWidth: '1000px',
              whiteSpace: 'nowrap',
              zIndex: 30,
              textShadow: '2px 2px 1px rgba(0, 0, 0, 0.2)'
            }}
            initial={{ opacity: 0, y: -80 }}
            animate={{ opacity: 1, y: -100, scale: 1.5 }}
            exit={{ opacity: 0, y: -160, scale: 2 }}
            transition={{ duration: 0.5 }}
          >
            The competitors are ready!
          </motion.h1>
        )}
      </AnimatePresence>
    </div>
  )
}

export default { Countdown, SprintTitle }
